import { PageContainer, PageHeader } from "@touche-lts/panel-ui/dist";
import { IconHelp } from "assets/icons";
import { ApiType, settings } from "config/settings";
import styles from "./help-page.module.scss";

import shopifyAddStore from "assets/images/shopify-add-store.webp";
import shopifyApiKey from "assets/images/shopify-api-key.webp";
import shopifyEditCode from "assets/images/shopify-edit-code.webp";
import shopifyInstallScript from "assets/images/shopify-install-script.webp";
import shopifyLogin from "assets/images/shopify-login.webp";
import shopifyOtp from "assets/images/shopify-otp.webp";
import shopifyResult from "assets/images/shopify-result.webp";
import shopifySignup from "assets/images/shopify-signup.webp";

const embedScript = `<!--------------------- Touche Video Player Embed Script ------------------- -->
<script>
  window.toucheConfig = {
    apiKey: "YOUR_API_KEY_HERE",
  };
</script>
<script src="${settings[ApiType.PLUGIN_URL]}" defer></script>
<!--------------------- End Touche Video Player Embed Script ------------------>`;

const HelpPageComponent = () => {
  return (
    <PageContainer>
      <PageHeader
        navSegments={[
          {
            icon: IconHelp,
            label: "Help",
            iconProps: { viewBox: "0 0 22 22" },
          },
        ]}
      />

      <div className={styles.helpContent}>
        <header className={styles.header}>
          <span className={styles.pill}>Shopify Integration Guide</span>
          <h1>Touche Plugin Setup Guide for Shopify</h1>
          <p>
            This document provides step-by-step instructions to integrate the
            Touche plugin into your Shopify store. Follow this guide to optimize
            your customer experience and manage interactive video data
            effectively.
          </p>
        </header>

        <main className={styles.page}>
          <aside className={styles.index} aria-label="Setup guide index">
            <h2>Guide Index</h2>
            <ol>
              <li>
                <a href="#prerequisites">Prerequisites</a>
              </li>
              <li>
                <a href="#account">Create Admin Account</a>
              </li>
              <li>
                <a href="#store-config">Store Configuration</a>
              </li>
              <li>
                <a href="#api-key">Get API Key</a>
              </li>
              <li>
                <a href="#install-script">Install Script</a>
              </li>
              <li>
                <a href="#activation">Activation &amp; Verification</a>
              </li>
              <li>
                <a href="#troubleshooting">Troubleshooting</a>
              </li>
            </ol>
          </aside>

          <article aria-label="Shopify setup guide">
            <section id="prerequisites">
              <h2>1. Prerequisites</h2>
              <ul>
                <li>Admin access to your Shopify store.</li>
                <li>
                  Your Shopify store URL (for example,
                  your-store.myshopify.com).
                </li>
              </ul>
            </section>

            <section id="account">
              <h2>2. Create a Touche Shopify Admin Account</h2>
              <div className={styles.steps}>
                <div className={styles.step}>
                  <h3>Step 2.1: Register Your Account</h3>
                  <ol>
                    <li>
                      Visit the Touche Registration Page:{" "}
                      <a
                        href={`${window.location.origin}/shopify/signup`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {`${window.location.origin}/shopify/signup`}
                      </a>
                    </li>
                    <li>
                      Fill in the required details: First Name, Email Address,
                      Password &amp; Confirm Password, Company Name, Shopify
                      Site URL.
                    </li>
                    <li>
                      Enter the base URL only (for example,
                      https://store.myshopify.com). Do not include a trailing
                      slash.
                    </li>
                    <li>
                      Click <strong>Sign up</strong>.
                    </li>
                  </ol>
                  <img
                    className={styles.stepImage}
                    src={shopifySignup}
                    style={{ maxWidth: "400px" }}
                    alt="Touche Shopify registration form"
                  />
                </div>

                <div className={styles.step}>
                  <h3>Step 2.2: Email Verification</h3>
                  <ul>
                    <li>
                      A verification code (OTP) will be sent to your registered
                      email address.
                    </li>
                    <li>
                      Enter the code on the verification page to activate your
                      account.
                    </li>
                  </ul>
                  <img
                    className={styles.stepImage}
                    src={shopifyOtp}
                    style={{ maxWidth: "400px" }}
                    alt="Email verification OTP screen"
                  />
                </div>

                <div className={styles.step}>
                  <h3>Step 2.3: Log In</h3>
                  <ol>
                    <li>
                      Access the Login Page:{" "}
                      <a
                        href={`${window.location.origin}/shopify/login`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {`${window.location.origin}/shopify/login`}
                      </a>
                    </li>
                    <li>
                      Enter your credentials to access the Touche Dashboard.
                    </li>
                  </ol>
                  <img
                    className={styles.stepImage}
                    style={{ maxWidth: "400px" }}
                    src={shopifyLogin}
                    alt="Touche Shopify login screen"
                  />
                </div>
              </div>
            </section>

            <section id="store-config">
              <h2>3. Store Configuration</h2>
              <ol>
                <li>
                  After logging in for the first time, you will be redirected to
                  the Create Store page.
                </li>
                <li>
                  Store Details: Enter your Store Name, Shopify Website URL, and
                  a brief Description.
                </li>
                <li>
                  Payment Details: Enter your billing information as required by
                  the platform.
                </li>
                <li>
                  Shopify Credentials: Go to your Shopify Dev Dashboard and
                  retrieve your API credentials, then enter them into the Touche
                  setup form.
                </li>
                <li>
                  Shipping Options: Create at least one shipping option to
                  proceed.
                </li>
              </ol>
              <div className={styles.note}>
                Note: Upon successful creation, your Shopify products will
                automatically synchronize with the Touche system.
              </div>
              <img
                className={styles.stepImage}
                src={shopifyAddStore}
                alt="Create store form in Touche Shopify setup"
              />
            </section>

            <section id="api-key">
              <h2>4. Get API Key &amp; Setup Touche Plugin</h2>
              <div className={styles.steps}>
                <div className={styles.step}>
                  <h3>Step 4.1: Retrieve Your API Key</h3>
                  <ol>
                    <li>Go to the Touche Settings Page.</li>
                    <li>
                      Locate the store you just created and copy the API Key.
                    </li>
                  </ol>
                  <img
                    className={styles.stepImage}
                    src={shopifyApiKey}
                    alt="API key location in Touche settings"
                  />
                </div>

                <div className={styles.step} id="install-script">
                  <h3>Step 4.2: Install the Script in Shopify</h3>
                  <ol>
                    <li>Log in to your Shopify Admin Dashboard.</li>
                    <li>
                      Navigate to <strong>Online Store &gt; Themes</strong>.
                    </li>
                    <li>
                      On your Current Theme, click the three dots (...) and
                      select <strong>Edit Code</strong>.
                    </li>
                    <li>
                      Open the <strong>theme.liquid</strong> file located in the{" "}
                      <strong>Layout</strong> folder.
                    </li>
                    <li>
                      Scroll to the bottom and paste the following script
                      immediately before the closing <code>&lt;/body&gt;</code>{" "}
                      tag:
                    </li>
                  </ol>
                  <img
                    className={styles.stepImage}
                    src={shopifyInstallScript}
                    alt="Shopify theme edit code entry point"
                  />
                  <pre className={styles.codeBlock}>
                    <code>{embedScript}</code>
                  </pre>
                  <img
                    className={styles.stepImage}
                    src={shopifyEditCode}
                    alt="Script inserted into theme.liquid"
                  />
                </div>

                <div className={styles.step}>
                  <h3>Step 4.3: Finalize</h3>
                  <ol>
                    <li>
                      Replace <code>YOUR_API_KEY_HERE</code> with the actual API
                      Key.
                    </li>
                    <li>
                      Click <strong>Save</strong>.
                    </li>
                  </ol>
                </div>
              </div>
            </section>

            <section id="activation">
              <h2>5. Activation &amp; Verification</h2>
              <ol>
                <li>
                  Visit your live Shopify website and navigate to the page
                  containing the video.
                </li>
                <li>
                  The script will automatically trigger the AI detection
                  process.
                </li>
                <li>
                  Open the Browser Console (F12 &gt; Console). You will see
                  status updates regarding the project.
                </li>
                <li>
                  Completion Sign: The process is complete when the console log
                  shows: <code>status: "PUBLISHED"</code> and{" "}
                  <code>autoObjectDetection: "COMPLETED"</code>.
                </li>
                <li>
                  Once published, your products will become interactive and
                  visible within the video player.
                </li>
              </ol>
              <img
                className={styles.stepImage}
                src={shopifyResult}
                alt="Successful publish status in browser console"
              />
            </section>

            <section id="troubleshooting">
              <h2>6. Troubleshooting</h2>
              <ul>
                <li>
                  API Key Verification: Ensure the API key is correctly enclosed
                  in single quotes.
                </li>
                <li>
                  URL Formatting: Ensure the Store URL in Touche settings does
                  not have a trailing <code>/</code>.
                </li>
                <li>
                  Video Tag: The script targets the <code>&lt;video&gt;</code>{" "}
                  tag found on the page. Ensure your theme or page content
                  includes a standard HTML5 video element.
                </li>
                <li>
                  Console Logs: Check the browser console (F12) for{" "}
                  <code>Touche App loaded</code> to confirm the integration is
                  active.
                </li>
              </ul>
            </section>

            <footer className={styles.footer}>
              Last updated: February 5, 2026
            </footer>
          </article>
        </main>
      </div>
    </PageContainer>
  );
};

const HelpPage = HelpPageComponent;
export { HelpPage };
